/** Narrowing the census to one site and/or one window state, from the URL (Dashboards' drills).
 *
 * The Data health dashboard counts windows by state and sites by gaps, and every mark opened
 * Coverage unfiltered: the reader landed on the whole census and had to find the thing the mark
 * had just counted. `site` (a facility id, the same fact the register's site filter holds) and
 * `state` (one of the census's six window states) now travel in the link and are applied here.
 *
 * **A value that names nothing is not in force, and the screen says so** - the register's
 * `?status=banana` rule. A site that has no declared stream in this window, or a state the census
 * does not have, would otherwise narrow a complete census to an empty one and read as "nothing is
 * expected", which is the one wrong answer this screen must never give. `ignored` carries the
 * values that were dropped so the banner can print them.
 *
 * **It narrows streams, never windows.** A stream with one missing month still shows its other
 * eleven: a grid row with eleven cells hidden would not be the stream's own record, and the cell
 * states are exactly what the reader came to see. The census counts at the head of the screen are
 * for the whole window, and the banner says how many streams the narrowing leaves.
 */

import type { Coverage } from "../api/sources";
import { STATE } from "./sourceModel";

export interface CoverageNarrowing {
  /** The census with only the streams that match; every other field is the server's. */
  data: Coverage;
  /** The site in force, with the name the census gave it. */
  site?: { id: string; name: string };
  /** The window state in force, one of `STATE`'s keys. */
  state?: string;
  /** Values asked for that matched nothing, as `site` / `state` and the text given. */
  ignored: ReadonlyArray<{ key: "site" | "state"; value: string }>;
  /** Whether anything narrowed the list. */
  active: boolean;
}

export function narrowCoverage(
  data: Coverage,
  asked: { site?: string; state?: string },
): CoverageNarrowing {
  const ignored: Array<{ key: "site" | "state"; value: string }> = [];

  let site: CoverageNarrowing["site"];
  if (asked.site) {
    const hit = data.sources.find((source) => source.facility_id === asked.site);
    if (hit) site = { id: hit.facility_id, name: hit.facility_name };
    else ignored.push({ key: "site", value: asked.site });
  }

  let state: string | undefined;
  if (asked.state) {
    if (Object.prototype.hasOwnProperty.call(STATE, asked.state)) state = asked.state;
    else ignored.push({ key: "state", value: asked.state });
  }

  const sources = data.sources.filter(
    (source) =>
      (!site || source.facility_id === site.id) &&
      (!state || source.windows.some((window) => window.state === state)),
  );
  return {
    data: site || state ? { ...data, sources } : data,
    site,
    state,
    ignored,
    active: Boolean(site || state),
  };
}
